// AIMS MUI theme — built from the design tokens (tokens.js).
// makeTheme(mode) returns a createTheme instance for 'light' | 'dark'.
// MUI palette uses literal hex (not CSS vars) so MUI's colour maths (contrast,
// hover shades) work correctly. Custom components still read var(--token) for
// the soft-panel surfaces; this keeps MUI controls visually in sync.

import { createTheme } from '@mui/material/styles';
import { tokensByMode, radius, shadow, fontSans, fontMono } from './tokens';

export function makeTheme(mode = 'light') {
  const t = tokensByMode[mode] || tokensByMode.light;
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: { main: t.primary, dark: t['primary-hover'], contrastText: t['on-primary'] },
      success: { main: t.pass },
      error: { main: t.fail },
      warning: { main: t.warn },
      info: { main: t.info },
      text: { primary: t.text, secondary: t.muted, disabled: t.faint },
      divider: t.border,
      background: { default: t.bg, paper: t.surface },
    },
    shape: { borderRadius: radius.md },
    typography: {
      fontFamily: fontSans,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
      // expose mono for components that want theme.typography.mono
      mono: { fontFamily: fontMono },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.sm, fontWeight: 600 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // Kill MUI's dark-mode elevation overlay (a translucent-white background-image that
          // lightens elevated Paper/Dialog/Menu). It clashes with our flat surface tokens — e.g. a
          // Dialog's Paper rendered lighter than a sticky-footer Box using raw var(--surface),
          // producing a visible "band" around the footer. Surfaces are distinguished by tokens + borders.
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: radius.md },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            boxShadow: isDark ? shadow.smDark : shadow.sm,
            border: `1px solid ${t.border}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.pill, fontWeight: 600 },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.bg, color: t.text },
        },
      },
    },
  });
}

export default makeTheme;
